import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { initializeApp } from "firebase/app";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { firebaseConfig } from "../utils/firebaseConfig";
import "./../App.css";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/context";
import {
  getFormattedDate,
  getIntervals,
  getTimeFromDate,
  getWeekDayAsNumber,
  postRequest,
} from "../utils/functions";
import notificationSvc from "../utils/notificationSvc";
const ConfirmServices = () => {
  const app = initializeApp(firebaseConfig);
  const db = getFirestore(app);
  const stripe = useStripe();
  const elements = useElements();
  const card = useRef();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const { cart, selected, setSelected } = useAuth();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    const isLoggedIn = checkAuth();
    if (!cart.length > 0) {
      return navigate("/services");
    }
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    getLocation();
    setSelected({ ...selected, name: user.displayName, email: user.email });
  }, []);

  const getLocation = async () => {
    const querySnapshot = await getDoc(doc(db, "Config", "Profile"));
    setLocations(querySnapshot.data().Location);
  };

  const handleLocationChange = async (e) => {
    setSelected({ ...selected, location: e });
    getTeams(e.LocationID);
  };

  const handleEmployeeChange = async (e) => {
    setSelected({ ...selected, slot: null });
    if (!selected.date) {
      return notificationSvc.error("Please select date first");
    }

    if (!e.id) {
      const duration = cart.reduce(
        (accumulator, currentValue) => accumulator + currentValue.Duration,
        0
      );
      const ints = getIntervals("10:00", "19:00", duration);
      setSlots(ints);
      setSelected({ ...selected, employee: e });
      return;
    }
    const schedule = e?.Schedule.find(
      (sch) => sch.Day === getWeekDayAsNumber(selected.date)
    );
    if (schedule?.IsClosed) {
      return notificationSvc.error(
        `${e.Name} is not available on selected date`
      );
    }
    setSelected({ ...selected, employee: e });
    getSlots(e?.id, schedule);
  };

  const isEmployeeAvailable = (employee, slot) => {
    const schedule = employee?.Schedule.find(
      (sch) => sch.Day === getWeekDayAsNumber(selected.date)
    );
    if (schedule?.IsClosed) {
      return false;
    }
    const restrictedStartTime = new Date(
      `${getFormattedDate(selected.date)}T${schedule.Start}`
    );
    const startTime = new Date(
      `${getFormattedDate(selected.date)} ${slot.startTime}`
    );
    const restrictedEndTime = new Date(
      `${getFormattedDate(selected.date)} ${schedule.End}`
    );
    const endTime = new Date(
      `${getFormattedDate(selected.date)} ${slot.endTime}`
    );
    if (startTime < restrictedStartTime || endTime > restrictedEndTime) {
      return false; // Time slot falls outside restricted time range
    }
    return true;
  };

  const handleAnyEmployee = async (e) => {
    const querySnapshot = await getDocs(
      query(
        collection(db, "Bookings"),
        where("StartDate", "==", getFormattedDate(selected.date))
      )
    );
    let active = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.Active) {
        return;
      }
      active.push({
        ...data,
        startTime: getTimeFromDate(data.StartTime?.toDate()),
        endTime: getTimeFromDate(data.EndTime?.toDate()),
      });
    });
    for (let index = 1; index < employees.length; index++) {
      const employee = employees[index];
      const bookings = active.filter((book) => book.EmployeeID === employee.id);
      if (!isEmployeeAvailable(employee, e)) {
        if (employees.length === index + 1) {
          notificationSvc.error(
            `No team is available on selected date in selected slot`
          );
          break;
        }
        continue;
      }
      const isAvailable = isTimeSlotAvailable(e, bookings);
      if (!isAvailable) {
        if (employees.length === index + 1) {
          notificationSvc.error(
            `No team is available on selected date in selected slot`
          );
          break;
        }
        continue;
      }
      setSelected({ ...selected, anyEmployee: employee, slot: e });
      break;
    }
  };

  const getSlots = async (id, schedule) => {
    const duration = cart.reduce(
      (accumulator, currentValue) => accumulator + currentValue.Duration,
      0
    );
    const querySnapshot = await getDocs(
      query(
        collection(db, "Bookings"),
        where("StartDate", "==", getFormattedDate(selected.date))
      )
    );
    let active = [];
    querySnapshot.forEach((doc) => {
      if (doc.data().EmployeeID === id) {
        const data = doc.data();
        active.push({
          startTime: getTimeFromDate(data.StartTime?.toDate()),
          endTime: getTimeFromDate(data.EndTime?.toDate()),
        });
      }
    });
    active.sort((a, b) => (a.startTime > b.startTime ? 1 : -1));
    let intervals = [];
    const nInt = getIntervals(
      schedule?.Start,
      active.length > 0 ? active[0].startTime : schedule.End,
      duration
    );
    if (nInt.length > 0) {
      nInt.forEach((interval) => {
        intervals.push(interval);
      });
    }
    for (let i = 0; i < active.length; i++) {
      const int = getIntervals(
        active[i].endTime,
        active[i + 1] ? active[i + 1].startTime : schedule.End,
        duration
      );
      if (int.length > 0) {
        int.forEach((interval) => {
          intervals.push(interval);
        });
      }
    }
    handleSlots(active, intervals);
  };

  const isTimeSlotAvailable = (slot, timeSlots) => {
    const startTime = new Date(
      `${getFormattedDate(selected.date)} ${slot.startTime}`
    );
    const endTime = new Date(
      `${getFormattedDate(selected.date)} ${slot.endTime}`
    );
    return !timeSlots.some((slot) => {
      const slotStartTime = new Date(
        `${getFormattedDate(selected.date)} ${slot.startTime}`
      );
      const slotEndTime = new Date(
        `${getFormattedDate(selected.date)} ${slot.endTime}`
      );
      return (
        (slotStartTime <= startTime && slotEndTime > startTime) ||
        (slotStartTime < endTime && slotEndTime >= endTime) ||
        (slotStartTime >= startTime && slotEndTime <= endTime)
      );
    });
  };

  const handleSlots = (active, intervals) => {
    setSlots(intervals);
  };

  const checkAuth = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user;
  };

  const getTeams = async (locID) => {
    const querySnapshot = await getDocs(
      query(collection(db, "Employees"), where("LocationID", "==", locID))
    );
    let emp = [{ id: null, Name: "Any" }];
    querySnapshot.forEach((doc) => {
      if (doc.data() && !doc.data().IsWalkIn)
        emp.push({ ...doc.data(), id: doc.id });
    });
    setEmployees(emp);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!selected.location || !selected.employee || !selected.slot) {
      notificationSvc.error("All fields are required");
      setLoading(false);
      return;
    }
    const billingDetails = {
      name: selected.name,
      email: user.email,
    };
    if (selected.payInStore) {
      navigate("/confirm-services", { state: { method: "PayInStore" } });
      setLoading(false);
      return;
    }

    try {
      stripe
        .createPaymentMethod({
          type: "card",
          billing_details: billingDetails,
          card: elements.getElement(CardElement),
        })
        .then((resp) => {
          postRequest("/payment/method/attach", {
            paymentMethod: resp.paymentMethod.id,
            customerId: user.StripeCustomerID,
          })
            .then((resp) => {
              navigate("/confirm-services", { state: resp.data });
              setLoading(false);
            })
            .catch((err) => {
              notificationSvc.error("Failed to validate payment info");
              setLoading(false);
              return;
            });
          return;
        });
    } catch (err) {
      console.log(err);
      notificationSvc.error(err.message);
      setLoading(false);
    }
  };

  const handleTimeSlot = (e) => {
    if (!selected.employee.id) {
      handleAnyEmployee(e);
      return;
    }
    setSelected({ ...selected, slot: e });
  };

  return (
    <>
      <Header />
      <section className="mt-5 mb-5 pt-5 pb-5">
        <div className="container">
          <div className="row">
            <div className="col-md-10 offset-md-1">
              <div className="form_box p-5">
                <span className="login100-form-title pb-4 pt-4">
                  Book an Appointment
                </span>
                <form
                  className="login100-form validate-form"
                  onSubmit={(e) => handleSubmit(e)}
                >
                  <div className="row">
                    <div className="col-md-6">
                      <div
                        className="wrap-input100 validate-input mb-3"
                        data-validate="Date is required"
                      >
                        <span className="label-input100">Date</span>
                        <DatePicker
                          selected={selected.date}
                          minDate={new Date()}
                          onChange={(e) =>
                            setSelected({ ...selected, date: e })
                          }
                          className="input100 px-1"
                          placeholderText="Select appointment date"
                        />
                        {/* <input
                          className="input100 px-1"
                          type="date"
                          name="date"
                          min={new Date()}
                          placeholder="date"
                        /> */}
                        <span className="focus-input100" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="wrap-input100 validate-input mb-3">
                        <span className="label-input100">location</span>
                        <Select
                          options={locations}
                          value={selected.location}
                          onChange={(e) => handleLocationChange(e)}
                          getOptionLabel={(e) => e.Address1}
                          getOptionValue={(e) => e.LocationID}
                        />
                        <span className="focus-input100" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="wrap-input100 validate-input mb-3">
                        <span className="label-input100">Team</span>
                        <Select
                          className="new-class"
                          value={selected.employee}
                          onChange={(e) => handleEmployeeChange(e)}
                          noOptionsMessage={() =>
                            !selected.location
                              ? "Please select location"
                              : "No team found"
                          }
                          options={employees.filter((emp) => !emp.id)}
                          isClearable
                          getOptionLabel={(e) => e.Name}
                          getOptionValue={(e) => e.id}
                        />
                        <span className="focus-input100" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        className="wrap-input100 validate-input mb-3"
                        data-validate
                      >
                        <span className="label-input100">Available Slot</span>
                        <Select
                          autoFocus
                          options={slots}
                          noOptionsMessage={() =>
                            !selected.employee || !selected.date
                              ? "Please select Team and Appointment date"
                              : "No timeslot availabe"
                          }
                          value={selected.slot}
                          onChange={(e) => handleTimeSlot(e)}
                          getOptionLabel={(e) => e.startTime}
                          getOptionValue={(e) => e.startTime}
                        />
                        <span className="focus-input100" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        className="wrap-input100 validate-input mb-3"
                        data-validate="Date is required"
                      >
                        <span className="label-input100">Name</span>
                        <input
                          className="input100 px-1"
                          required
                          value={selected.name}
                          onChange={(e) =>
                            setSelected({ ...selected, name: e.target.value })
                          }
                          type="text"
                          name="Name"
                          placeholder="John"
                        />
                        <span className="focus-input100" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        className="wrap-input100 validate-input mb-3"
                        data-validate="Date is required"
                      >
                        <span className="label-input100">Phone</span>
                        <input
                          className="input100 px-1"
                          type="text"
                          required
                          value={selected.phoneNumber}
                          onChange={(e) =>
                            setSelected({
                              ...selected,
                              phoneNumber: e.target.value,
                            })
                          }
                          name="phone"
                          placeholder="+1 1234 1234"
                        />
                        <span className="focus-input100" />
                      </div>
                    </div>
                    <div className="col-md-6">
                    </div>
                    <div className="col-md-6 mb-3">
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <input
                          style={{ width: "40px", height: "40px"}}
                          checked={selected.payInStore}
                          onChange={() =>
                            setSelected({
                              ...selected,
                              payInStore: !selected.payInStore,
                            })
                          }
                          type="checkbox" width="40px" height="40px"
                        />
                        Pay in Store
                      </div>
                    </div>

                    {!selected.payInStore && (
                      <div className="col-md-12">
                        <div
                          className="validate-input mb-3"
                          data-validate="Date is required"
                        >
                          {/* <span className="label-input100">
                            Payment Details
                          </span> */}
                          {/* <CreditCardInput
                          cardCVCInputRenderer={({
                            handleCardCVCChange,
                            props,
                          }) => (
                            <input
                              {...props}
                              className="input100 px-1"
                              value={selected.cvc}
                              onChange={handleCardCVCChange((e) =>
                                setSelected({
                                  ...selected,
                                  cvc: e.target.value,
                                })
                              )}
                            />
                          )}
                          cardExpiryInputRenderer={({
                            handleCardExpiryChange,
                            props,
                          }) => (
                            <input
                              className="input100 px-1"
                              {...props}
                              value={selected.expiry}
                              onChange={handleCardExpiryChange((e) =>
                                setSelected({
                                  ...selected,
                                  expiry: e.target.value,
                                })
                              )}
                            />
                          )}
                          invalidClassName="invalid-class"
                          cardNumberInputRenderer={({
                            handleCardNumberChange,
                            props,
                          }) => (
                            <input
                              {...props}
                              className="input100 px-1"
                              value={selected.cardNumber}
                              onChange={handleCardNumberChange((e) =>
                                setSelected({
                                  ...selected,
                                  cardNumber: e.target.value,
                                })
                              )}
                            />
                          )}
                        /> */}
                          {/* <CardElement options={{ hidePostalCode: true }} /> */}
                          {/* <input class="input100 px-1" type="text" name="phone" placeholder="+1 1234 1234"> */}
                          {/* <span className="focus-input100" /> */}
                        </div>
                      </div>
                    )}
                    <div className="col-md-12">
                      <div
                        className="wrap-input100 validate-input mb-3"
                        data-validate="Date is required"
                      >
                        <span className="label-input100">Note</span>
                        <textarea
                          value={selected.Notes}
                          onChange={(e) =>
                            setSelected({ ...selected, Notes: e.target.value })
                          }
                          className="input100 px-1"
                          rows={8}
                          defaultValue={""}
                        />
                        {/* <input class="input100 px-1" type="text" name="phone" placeholder="+1 1234 1234"> */}
                        <span className="focus-input100" />
                      </div>
                    </div>
                  </div>
                  <div className="container-login100-form-btn">
                    <div className="wrap-login100-form-btn mb-3">
                      <div className="login100-form-bgbtn" />
                      <button
                        type="button"
                        onClick={() => navigate("/services")}
                        className="login100-form-btn log_sub"
                      >
                        Back
                      </button>
                    </div>
                    <div className="wrap-login100-form-btn">
                      <div className="login100-form-bgbtn" />
                      <button
                        disabled={loading}
                        type="submit"
                        className="login100-form-btn log_sub"
                      >
                        {loading ? "Processing please wait..." : "Next"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ConfirmServices;
